import React from 'react';
import styled from 'styled-components';
import { useLocation, Link } from 'react-router-dom';
import { IconGrid } from '../../ui/IconGrid/IconGrid';
import { useApi } from '../../../hooks/useApi';
import { LinkGroup, PageData, QuickLinkItem, TextLink } from '../../../types/api';
import { LOADING_MESSAGES, ERROR_MESSAGES } from '../../../utils/constants';

const Container = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.wide};
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.sm};
`;

const PageTitle = styled.h1`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const IntroText = styled.p`
  font-size: 1.1rem;
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text};
`;

const TextLinksSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const LinkGroupSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const TextLinksList = styled.ul`
  list-style: none;
  padding: 0;
  max-width: 600px;
  margin: 0 auto;
  
  li {
    margin-bottom: 5px; 
  }
  li:first-child {
    font-weight: bold;
    margin-bottom: ${({ theme }) => theme.spacing.md};
  } 

  a {
    padding: 2px;
  }
`;

const TextLinkItem = styled(Link)`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.accent};
  text-decoration: none;
  display: block;
  padding: ${({ theme }) => theme.spacing.xs} 0;
  transition: ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.primary};
  }
  
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textLight};
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: #d32f2f;
  background-color: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 4px;
  margin: ${({ theme }) => theme.spacing.md} 0;
`;

export const SectionPage: React.FC = () => {
  const location = useLocation();

  // Map URL paths to section IDs
  const pathToSection: { [key: string]: string } = {
    '/my-diabetes': 'my-diabetes',
    '/medical': 'medical',
    '/info-for-whanau': 'what-is-diabetes',
    '/links': 'links',
    '/regions': 'regions'
  };

  const section = pathToSection[location.pathname];

  // Only make API call if we have a valid section - use useMemo to prevent unnecessary re-renders
  const apiEndpoint = React.useMemo(() => {
    return section ? `/pages/${section}.json` : null;
  }, [section]);

  const { data: pageData, loading, error } = useApi<PageData>(apiEndpoint || '', {
    immediate: !!apiEndpoint
  });

  // Handle unknown section
  if (!section) {
    return (
      <Container>
        <ErrorContainer>
          Page not found: {location.pathname}
        </ErrorContainer>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <LoadingContainer>{LOADING_MESSAGES.PAGE}</LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>
          {ERROR_MESSAGES.LOADING_ERROR}: {error}
        </ErrorContainer>
      </Container>
    );
  }

  if (!pageData) {
    return (
      <Container>
        <ErrorContainer>{ERROR_MESSAGES.PAGE_NOT_FOUND}</ErrorContainer>
      </Container>
    );
  }

  const content = pageData.content as any; // Type assertion for section page content

  return (
    <Container>
      <PageTitle>{pageData.title}</PageTitle>

      {content.intro && (
        <IntroText>{content.intro}</IntroText>
      )}

      {content.iconGrid && content.iconGrid.length > 0 && (
        <IconGrid items={content.iconGrid as QuickLinkItem[]} />
      )}

      {content.linkGroups && content.linkGroups.length > 0 && (
        <LinkGroupSection>
          {(content.linkGroups as LinkGroup[]).map((linkGroup: any, groupIndex: number) => (
            <TextLinksSection key={`linkgroup-${groupIndex}`}>
              <TextLinksList>
                <li key="ttl">{linkGroup.title}</li>
                {(linkGroup.links as TextLink[]).map((link, linkIndex) => (
                  <li key={`${groupIndex}-${linkIndex}-${link.title}`}>
                    <TextLinkItem to={link.link}>
                      {link.title}
                    </TextLinkItem>
                  </li>
                ))}
              </TextLinksList>
            </TextLinksSection>
          )
          )}
        </LinkGroupSection>
      )}

      {content.textLinks && content.textLinks.length > 0 && (
        <TextLinksSection>
          <TextLinksList>
            {(content.textLinks as TextLink[]).map((link, index) => (
              <li key={`textlink-${index}-${link.title}`}>
                <TextLinkItem to={link.link}>
                  {link.title}
                </TextLinkItem>
              </li>
            ))}
          </TextLinksList>
        </TextLinksSection>
      )}
    </Container>
  );
};

export default SectionPage;
